import React from 'react';
import ReactMarkdown from 'react-markdown';
import AnalysisResult, { isAnalysisPayload } from '../AnalysisResult';
import ComplianceResult, { isCompliancePayload } from './ComplianceResult';
import VendorMatchResult, { isVendorMatchPayload } from './VendorMatchResult';
import MonitorResult, { isMonitorPayload } from './MonitorResult';

/**
 * Renders whatever an agent returned, in the best form available.
 *
 * Detection keys off payload SHAPE, not agentType, for two reasons: the agent
 * names do not read the way the UI labels do (contract-scout powers Analyze),
 * and an agent can change what it returns without the renderer being told. An
 * unrecognised shape falls back to text rather than breaking — which is how a
 * structured report ended up rendering as raw JSON before these existed.
 */
export function extractAgentText(raw: string | undefined): string {
  if (!raw) return 'No result data';
  try {
    const parsed = JSON.parse(raw);
    if (parsed?.result?.payloads?.length > 0) {
      return parsed.result.payloads.map((p: any) => p.text || '').filter(Boolean).join('\n\n');
    }
    if (parsed?.payloads?.length > 0) {
      return parsed.payloads.map((p: any) => p.text || '').filter(Boolean).join('\n\n');
    }
    return (
      parsed.content || parsed.response || parsed.analysis || parsed.message || parsed.text ||
      JSON.stringify(parsed, null, 2)
    );
  } catch {
    return raw;
  }
}

/**
 * Structured agents on the harness answer inside the text envelope, usually
 * as a ```json fence (Haiku does this even when told not to). Unwrap the fence
 * and parse, so the shape detectors below see the object, not the string —
 * otherwise Analyze and Compliance render as a raw JSON code block.
 */
export function parseEmbeddedJson(text: string | undefined): any | null {
  if (!text) return null;
  const trimmed = text.trim();
  const fenced = trimmed.match(/^```(?:json)?\s*([\s\S]*?)\s*```/i);
  const body = fenced ? fenced[1] : trimmed;
  if (!/^[\[{]/.test(body)) return null;
  try {
    return JSON.parse(body);
  } catch {
    // Truncated or trailing prose after the object: take the outermost object.
    const start = body.indexOf('{');
    const end = body.lastIndexOf('}');
    if (start >= 0 && end > start) {
      try {
        return JSON.parse(body.slice(start, end + 1));
      } catch {
        /* genuinely not JSON */
      }
    }
    return null;
  }
}

const AgentResultView: React.FC<{ result: string | undefined }> = ({ result }) => {
  let parsed: any = null;
  try {
    parsed = result ? JSON.parse(result) : null;
  } catch {
    /* not JSON — falls through to text */
  }

  // Some agents wrap their payload one level deep.
  const candidate = parsed && typeof parsed === 'object' && parsed.result ? parsed.result : parsed;
  const text = extractAgentText(result);
  const embedded = parseEmbeddedJson(text);

  for (const shape of [embedded, candidate, parsed]) {
    if (!shape) continue;
    if (isCompliancePayload(shape)) return <ComplianceResult data={shape} />;
    if (isVendorMatchPayload(shape)) return <VendorMatchResult data={shape} />;
    if (isMonitorPayload(shape)) return <MonitorResult data={shape} />;
    if (isAnalysisPayload(shape)) return <AnalysisResult data={shape} />;
  }

  if (!text.trim() || text === 'No result data') {
    // A completed task with nothing in it used to render as an empty box.
    return <p className="text-sm text-content-muted">The agent returned no output. Run it again.</p>;
  }

  // Text-envelope agents (Market Analysis and anything else returning
  // `payloads[].text`) write markdown — headings, bold, numbered lists. Shown
  // preformatted, the reader sees literal ### and ** instead of structure.
  // Anything that isn't markdown still renders correctly through the same path.
  return (
    <div className="markdown prose prose-sm max-w-none">
      <ReactMarkdown>{text}</ReactMarkdown>
    </div>
  );
};

export default AgentResultView;
